/**
 * LeaveGuardDialog — intercepts navigation away from Practice/Quiz.
 * Rule 4: Leave guard active on Practice and Quiz screens only.
 * Shows a confirmation dialog before allowing navigation away from an active session.
 *
 * Phase 8 will wire up useSessionState to detect a live session and implement
 * in-app navigation blocking. useBlocker was removed (D-027) because React Router
 * v7's BrowserRouter does not expose getBlocker/deleteBlocker on its internal router,
 * causing a TypeError on every Practice/Quiz mount.
 *
 * Current behaviour: isDirty is always false (useSessionState stub returns idle),
 * so the dialog never shows. The beforeunload handler still protects hard refreshes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSessionState }  from '@/hooks/useSessionState'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { useFocusTrap }     from '@/hooks/useFocusTrap'
import { scaleIn }          from '@/lib/motion-variants'

export function LeaveGuardDialog() {
  const { status, answers, saveSessionState } = useSessionState()
  const reducedMotion = useReducedMotion()
  const [showDialog, setShowDialog] = useState(false)

  // Only block when there's an active session with answers entered
  const isDirty = status === 'active' && answers.length > 0

  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, showDialog)

  // ── In-app navigation blocking ─────────────────────────────────────────────
  // Phase 8: replace with React Router v7–compatible blocking once useSessionState
  // is wired to live quiz state. useBlocker (RR v6 API) is NOT used here because
  // RR v7's BrowserRouter doesn't provide getBlocker/deleteBlocker (D-027).
  const handleStay = useCallback(() => {
    setShowDialog(false)
  }, [])

  const handleLeave = useCallback(() => {
    saveSessionState()
    setShowDialog(false)
  }, [saveSessionState])

  // ── Escape key closes dialog ────────────────────────────────────────────────
  useEffect(() => {
    if (!showDialog) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleStay()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [showDialog, handleStay])

  // ── Hard refresh / tab-close guard ─────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      saveSessionState()
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty, saveSessionState])

  return (
    <AnimatePresence>
      {showDialog && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 z-40"
            onClick={handleStay}
            aria-hidden="true"
          />

          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="leave-guard-title"
            aria-describedby="leave-guard-desc"
            variants={reducedMotion ? {} : scaleIn}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50
                       bg-surface rounded-[20px] p-6 shadow-modal
                       max-w-sm mx-auto"
          >
            <h2
              id="leave-guard-title"
              className="font-heading text-lg font-bold text-text-primary mb-2"
            >
              Leave this session?
            </h2>
            <p
              id="leave-guard-desc"
              className="text-text-body text-sm mb-6 leading-relaxed"
            >
              Your progress so far will be saved. You can resume later.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleStay}
                className="btn-primary w-full"
                autoFocus
              >
                Keep going
              </button>
              <button
                onClick={handleLeave}
                className="btn-ghost w-full"
              >
                Save and leave
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
